import React, { useEffect, useState, createElement, Component } from 'react';
import { Check, Star, Shield, ChevronDown, Sparkles, Heart, Zap, Eye, RefreshCw, Smartphone, Mail, Infinity as InfinityIcon, Flame } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SocialProofPopup } from '../components/SocialProofPopup';
// --- Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-18">
    {children}
  </motion.div>;
const CHECKOUT_URL = 'https://www.claridaddirecta.com/cart/57859915579773:1';
const Button = ({
  children,
  variant = 'primary',
  className = '',
  fullWidth = false,
  onClick
}: {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'white';
  className?: string;
  fullWidth?: boolean;
  onClick?: () => void;
}) => {
  const baseStyles = 'inline-flex items-center justify-center px-8 py-4 text-base font-sans font-semibold uppercase tracking-[0.5px] transition-all duration-300 transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-full shadow-lg';
  const variants = {
    primary: 'bg-gradient-to-r from-violet-600 to-rose-400 text-white hover:shadow-violet-300/50 focus:ring-violet-500',
    secondary: 'bg-white text-violet-700 hover:bg-gray-50 focus:ring-white/50',
    outline: 'border-2 border-violet-600 text-violet-700 hover:bg-violet-50 focus:ring-violet-500',
    white: 'bg-white text-violet-800 hover:bg-gray-100 shadow-xl'
  };
  return <button className={`${`${baseStyles} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`} text-[12px]`} onClick={onClick} data-id="element-19">
      {children}
    </button>;
};
const AccordionItem = ({
  question,
  answer,
  isOpen,
  onClick
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) => <div className="border-b border-violet-100 last:border-0" data-id="element-20">
    <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" onClick={onClick} data-id="element-21">
      <span className="font-sans font-medium text-lg text-gray-800 group-hover:text-violet-700 transition-colors" style={{
      lineHeight: '1.3'
    }} data-id="element-22">
        {question}
      </span>
      <span className={`ml-4 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-23">
        <ChevronDown className="w-5 h-5 text-violet-400" data-id="element-24" />
      </span>
    </button>
    <AnimatePresence data-id="element-25">
      {isOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="overflow-hidden" data-id="element-26">
          <p className="pb-6 font-sans font-normal text-gray-600 max-w-[680px]" style={{
        lineHeight: '1.6'
      }} data-id="element-27">
            {answer}
          </p>
        </motion.div>}
    </AnimatePresence>
  </div>;
const TestimonialCard = ({
  name,
  location,
  quote,
  delay
}: {
  name: string;
  location: string;
  quote: string;
  delay: number;
}) => <FadeIn delay={delay} className="h-full" data-id="element-28">
    <div className="bg-white p-8 rounded-2xl shadow-lg border border-violet-50 h-full flex flex-col relative overflow-hidden group hover:shadow-xl transition-shadow duration-300" data-id="element-29">
      <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-30">
        <Sparkles className="w-24 h-24 text-violet-500" data-id="element-31" />
      </div>
      <div className="flex gap-1 mb-4" data-id="element-32">
        {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-33" />)}
      </div>
      <p className="font-sans font-normal text-gray-600 italic mb-6 flex-grow relative z-10" style={{
      lineHeight: '1.6'
    }} data-id="element-34">
        "{quote}"
      </p>
      <div className="flex items-center mt-auto" data-id="element-35">
        <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 font-sans font-semibold text-sm mr-3" data-id="element-36">
          {name.split(' ').map(n => n[0]).join('')}
        </div>
        <div data-id="element-37">
          <h4 className="font-sans font-semibold text-gray-900 text-sm" data-id="element-38">
            {name}
          </h4>
          <p className="font-sans font-normal text-xs text-gray-500" data-id="element-39">
            {location}
          </p>
        </div>
      </div>
    </div>
  </FadeIn>;
const BenefitCard = ({
  icon: Icon,
  title,
  description,
  delay
}: {
  icon: any;
  title: string;
  description: string;
  delay: number;
}) => <FadeIn delay={delay} className="h-full" data-id="element-40">
    <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-md border border-white/50 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full" data-id="element-41">
      <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mb-4 text-violet-600" data-id="element-42">
        <Icon className="w-6 h-6" data-id="element-43" />
      </div>
      <h3 className="text-xl font-sans font-medium text-gray-900 mb-2" style={{
      lineHeight: '1.3'
    }} data-id="element-44">
        {title}
      </h3>
      <p className="font-sans font-normal text-gray-600 text-sm" style={{
      lineHeight: '1.6'
    }} data-id="element-45">
        {description}
      </p>
    </div>
  </FadeIn>;
// --- Countdown Timer Component ---
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 1,
    minutes: 33,
    seconds: 49
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let {
          hours,
          minutes,
          seconds
        } = prev;
        if (seconds > 0) {
          seconds -= 1;
        } else if (minutes > 0) {
          minutes -= 1;
          seconds = 59;
        } else if (hours > 0) {
          hours -= 1;
          minutes = 59;
          seconds = 59;
        } else {
          // Reset to create urgency loop
          hours = 1;
          minutes = 33;
          seconds = 49;
        }
        return {
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatNumber = (num: number) => num.toString().padStart(2, '0');
  return <div className="flex items-center justify-center gap-3" data-id="element-46">
      <div className="flex flex-col items-center" data-id="element-47">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-center" data-id="element-48">
          <span className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-49">
            {formatNumber(timeLeft.hours)}
          </span>
        </div>
        <span className="text-xs text-gray-500 mt-2 uppercase tracking-wide font-medium" data-id="element-50">
          Horas
        </span>
      </div>
      <div className="flex flex-col items-center" data-id="element-51">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-center" data-id="element-52">
          <span className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-53">
            {formatNumber(timeLeft.minutes)}
          </span>
        </div>
        <span className="text-xs text-gray-500 mt-2 uppercase tracking-wide font-medium" data-id="element-54">
          Minutos
        </span>
      </div>
      <div className="flex flex-col items-center" data-id="element-55">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-center" data-id="element-56">
          <span className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-57">
            {formatNumber(timeLeft.seconds)}
          </span>
        </div>
        <span className="text-xs text-gray-500 mt-2 uppercase tracking-wide font-medium" data-id="element-58">
          Segundos
        </span>
      </div>
    </div>;
};
// --- Meta Pixel ---
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}
const META_PIXEL_ID = '1435718891550072';
// Generates a unique event ID for deduplication between browser pixel and Conversions API
const generateEventId = () => {
  return `evt_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
};
const initMetaPixel = () => {
  if (typeof window === 'undefined' || window.fbq) return;
  const fbq: any = function (...args: any[]) {
    fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args);
  };
  if (!window._fbq) window._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
};
// --- Main Page Component ---
export function AkashicLanding() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [showStickyBar, setShowStickyBar] = useState(false);
  useEffect(() => {
    initMetaPixel();
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToOffer = () => {
    // Fire custom Meta Pixel event: Click_Bajar_Pagina
    if (window.fbq) {
      window.fbq('trackCustom', 'Click_Bajar_Pagina', {
        button_location: 'landing_page'
      });
    }
    const element = document.getElementById('offer');
    if (element) element.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const goToCheckout = () => {
    const eventId = generateEventId();
    if (window.fbq) {
      // Standard Meta event for ad optimization
      window.fbq('track', 'InitiateCheckout', {
        value: 6.36,
        currency: 'USD',
        content_ids: ['57859915579773'],
        content_type: 'product',
        num_items: 1
      }, {
        eventID: eventId
      });
      // Custom event for internal tracking
      window.fbq('trackCustom', 'Click_Ir_Shopify', {
        value: 6.36,
        currency: 'USD',
        content_ids: ['57859915579773'],
        destination_url: CHECKOUT_URL
      }, {
        eventID: eventId
      });
    }
    // Small delay to ensure pixel fires before navigation
    setTimeout(() => {
      window.location.href = CHECKOUT_URL;
    }, 300);
  };
  return <div className="min-h-screen font-sans text-gray-800 bg-cream overflow-x-hidden" data-id="element-59">
      {/* SOCIAL PROOF POPUP */}
      <SocialProofPopup data-id="element-60" />

      {/* PROMOTIONAL TOP BANNER */}
      <div className="sticky top-0 z-50 w-full bg-gradient-to-r from-violet-600 via-purple-600 to-rose-500 flex items-center justify-center px-4 py-2.5 min-h-[44px]" data-id="element-61">
        <p className="text-white font-sans font-semibold text-sm md:text-base tracking-wide whitespace-nowrap flex items-center gap-2" data-id="element-62">
          🔥 <span data-id="element-63">Hoy —</span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-white text-violet-700 font-bold text-xs md:text-sm shadow-sm" data-id="element-64">
            75% OFF
          </span>
        </p>
      </div>

      {/* 1. HERO SECTION */}
      <section className="relative min-h-screen flex py-20 px-4 overflow-hidden bg-hero-gradient pt-[30px] pb-[30px] items-center justify-center" data-id="element-65">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-66">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-violet-200/20 blur-3xl" data-id="element-67" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-rose-200/20 blur-3xl" data-id="element-68" />
          <svg className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-[0.03]" viewBox="0 0 100 100" data-id="element-69">
            <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="0.5" fill="none" data-id="element-70" />
            <circle cx="50" cy="50" r="30" stroke="currentColor" strokeWidth="0.5" fill="none" data-id="element-71" />
            <circle cx="50" cy="50" r="20" stroke="currentColor" strokeWidth="0.5" fill="none" data-id="element-72" />
          </svg>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-73">
          <FadeIn data-id="element-74">
            <span className="inline-block px-4 py-1.5 rounded-full bg-violet-100 text-violet-800 text-sm font-sans font-semibold tracking-wide mb-6 uppercase" data-id="element-75">
              Curso Online Transformacional
            </span>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-76">
            <h1 className="text-[2.1rem] md:text-6xl lg:text-7xl font-serif font-semibold text-gray-900 mb-5 md:mb-6" style={{
            letterSpacing: '-0.3px',
            lineHeight: '1.12'
          }} data-id="element-77">
              <span className="block text-gray-800 mb-3 md:mb-0 md:inline font-[Montserrat,_sans-serif] text-[28px]" data-id="element-78">
                ¿Repites los mismos patrones?
              </span>
              <span className="hidden md:inline" data-id="element-79"> </span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-rose-500 md:text-6xl lg:text-7xl font-[Montserrat,_sans-serif] text-[28px]" style={{
              letterSpacing: '-0.2px',
              lineHeight: '1.18'
            }} data-id="element-80">
                ¿Sientes un vacío que nada llena?
              </span>
            </h1>
          </FadeIn>

          {/* Hero Image */}
          <FadeIn delay={0.15} data-id="element-81">
            <div className="mt-6 md:mt-8 mb-6 flex justify-center" data-id="element-82">
              <img src="https://cdn.magicpatterns.com/uploads/cDkZhiY9m333N4dFETETLG/ChatGPT_Image_22_feb_2026,_12_01_18.png" alt="Registros Akáshicos — Libro sagrado con geometría divina" className="w-[85%] md:w-full md:max-w-[560px] aspect-square object-cover object-center rounded-2xl" style={{
              boxShadow: '0 8px 40px 0 rgba(124, 58, 237, 0.13), 0 2px 12px 0 rgba(0,0,0,0.10)'
            }} data-id="element-83" />
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-84">
            <p className="md:text-xl font-sans font-normal text-gray-600 mb-6 w-[90%] max-w-2xl mx-auto mt-2.5 text-[16px]" style={{
            lineHeight: '1.65'
          }} data-id="element-85">
              Descubre las respuestas que tu alma guarda desde siempre. Aprende
              a abrir y leer tus propios{' '}
              <strong className="text-violet-700 font-semibold" data-id="element-86">
                Registros Akáshicos
              </strong>{' '}
              y transforma tu vida conectando con tu sabiduría interior.
            </p>
          </FadeIn>

          {/* Hero CTA — scrolls to offer */}
          <FadeIn delay={0.3} data-id="element-87">
            <div className="flex flex-col items-center mb-8" data-id="element-88">
              <Button onClick={scrollToOffer} className="mb-4 text-base px-10 py-5 shadow-violet-500/30 hover:shadow-violet-500/50" data-id="element-89">
                👉 QUIERO ACCEDER AHORA
              </Button>
              <div className="flex flex-col items-center gap-2 text-sm text-violet-200" data-id="element-90">
                <div className="flex items-center gap-2" data-id="element-91">
                  <div className="flex text-yellow-400" data-id="element-92">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-93" />)}
                  </div>
                  <span className="text-[14px] text-[#2E1065]" data-id="element-94">
                    4.9/5 — opiniones verificadas
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-emerald-300" data-id="element-95">
                  <Shield className="w-4 h-4" data-id="element-96" />
                  <span className="text-[14px]" data-id="element-97">
                    Garantía de 60 Días — Riesgo cero
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-98">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-10 text-left" data-id="element-99">
              {['Rompe ciclos repetitivos en tu vida', 'Encuentra claridad sobre tu propósito', 'Sana bloqueos emocionales profundos', 'Aprende a escuchar tu intuición'].map((item, i) => <div key={i} className="flex items-center bg-white/50 backdrop-blur-sm p-3 rounded-lg border border-white/60 shadow-sm" data-id="element-100">
                  <div className="flex-shrink-0 mr-3 bg-green-100 rounded-full p-1" data-id="element-101">
                    <Check className="w-4 h-4 text-green-600" data-id="element-102" />
                  </div>
                  <span className="font-sans font-normal text-gray-700" style={{
                lineHeight: '1.6'
              }} data-id="element-103">
                    {item}
                  </span>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 2. PAIN IDENTIFICATION SECTION */}
      <section className="py-20 px-4 bg-violet-50/50" data-id="element-104">
        <div className="max-w-3xl mx-auto" data-id="element-105">
          <FadeIn data-id="element-106">
            <h2 className="text-3xl md:text-4xl font-sans font-medium text-center text-gray-900 mb-6" style={{
            lineHeight: '1.3'
          }} data-id="element-107">
              ¿Te identificas con esto?
            </h2>
            <p className="text-center font-sans font-normal text-gray-600 mb-12 text-lg max-w-[680px] mx-auto" style={{
            lineHeight: '1.6'
          }} data-id="element-108">
              Tal vez sientes que algo falta en tu vida, aunque aparentemente
              todo esté bien…
            </p>
          </FadeIn>

          <div className="space-y-4" data-id="element-109">
            {['Sientes un vacío interior que nada parece llenar.', 'Repites patrones en relaciones y situaciones sin entender por qué.', 'Intuyes que existe algo más profundo, pero no sabes cómo acceder.', 'Cargas emociones que no parecen completamente tuyas.', 'Tomas decisiones desde el miedo en lugar de la claridad.'].map((pain, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-110">
                <div className="flex items-start p-4 bg-white rounded-xl shadow-sm border-l-4 border-violet-300 hover:border-violet-500 transition-colors" data-id="element-111">
                  <div className="mt-1 mr-4 flex-shrink-0" data-id="element-112">
                    <div className="w-2 h-2 rounded-full bg-violet-400" data-id="element-113" />
                  </div>
                  <p className="font-sans font-normal text-gray-700 text-lg" style={{
                lineHeight: '1.6'
              }} data-id="element-114">
                    {pain}
                  </p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.6} data-id="element-115">
            <p className="text-center italic text-violet-800 mt-12 font-semibold text-[16px] font-[Montserrat,_sans-serif]" style={{
            letterSpacing: '-0.5px',
            lineHeight: '1.3'
          }} data-id="element-116">
              "No estás roto. Solo estás desconectado de una parte profunda de
              ti."
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 3. COURSE PRESENTATION SECTION */}
      <section className="py-20 px-4 bg-white relative overflow-hidden" data-id="element-117">
        <div className="max-w-5xl mx-auto relative z-10" data-id="element-118">
          <div className="text-center mb-16" data-id="element-119">
            <FadeIn data-id="element-120">
              <h2 className="md:text-5xl font-semibold text-gray-900 mb-6 text-[28px] font-[Montserrat,_sans-serif]" style={{
              letterSpacing: '-0.5px',
              lineHeight: '1.1'
            }} data-id="element-121">
                Descubre Registros Akáshicos <br data-id="element-122" />
                <span className="text-violet-600" data-id="element-123">Sabiduría del Alma</span>
              </h2>
              <p className="font-sans font-normal text-gray-600 max-w-[680px] mx-auto text-[16px]" style={{
              lineHeight: '1.6'
            }} data-id="element-124">
                Un curso completo y transformador que te guía paso a paso para
                aprender a abrir, leer e interpretar tus propios Registros
                Akáshicos. No necesitas dones especiales ni experiencia previa.
                Solo la intención genuina de conectar contigo.
              </p>
              <div className="flex justify-center mt-10" data-id="element-125">
                <img src="https://cdn.magicpatterns.com/uploads/jmkhypQpf4Db69GKnhnmTp/imgi_2_mockup.webp" alt="Registros Akáshicos — Sabiduría del Alma en dispositivos" className="w-full max-w-[560px] rounded-2xl" style={{
                boxShadow: '0 8px 40px 0 rgba(124, 58, 237, 0.13), 0 2px 12px 0 rgba(0,0,0,0.08)'
              }} data-id="element-126" />
              </div>
            </FadeIn>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-127">
            {['Método paso a paso desde cero', 'Preparación energética segura', 'Meditaciones guiadas profundas', 'Interpretación clara de mensajes', 'Liberación de patrones emocionales', 'Aplicación práctica diaria'].map((benefit, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-128">
                <div className="flex items-center p-4 rounded-xl bg-violet-50/50 border border-violet-100" data-id="element-129">
                  <Sparkles className="w-5 h-5 text-violet-500 mr-3 flex-shrink-0" data-id="element-130" />
                  <span className="font-sans font-normal text-gray-800" style={{
                lineHeight: '1.6'
              }} data-id="element-131">
                    {benefit}
                  </span>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 4. RESULTS / TRANSFORMATION SECTION */}
      <section className="py-24 px-4 bg-spiritual-gradient" data-id="element-132">
        <div className="max-w-6xl mx-auto" data-id="element-133">
          <FadeIn data-id="element-134">
            <h2 className="text-3xl md:text-4xl font-sans font-medium text-center text-gray-900 mb-16" style={{
            lineHeight: '1.3'
          }} data-id="element-135">
              Lo que vas a lograr
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-136">
            <BenefitCard icon={Sparkles} title="Claridad Interior" description="Comprende tu propósito y toma decisiones con seguridad." delay={0.1} data-id="element-137" />
            <BenefitCard icon={RefreshCw} title="Sanación Kármica" description="Libera patrones repetitivos y ciclos emocionales." delay={0.2} data-id="element-138" />
            <BenefitCard icon={Heart} title="Transformación Personal" description="Reconecta con tu verdadero ser y esencia pura." delay={0.3} data-id="element-139" />
            <BenefitCard icon={Eye} title="Conexión Espiritual" description="Desarrolla tu intuición y conecta con tu guía interior." delay={0.4} data-id="element-140" />
            <BenefitCard icon={Zap} title="Sanación Emocional" description="Libera emociones estancadas que bloquean tu avance." delay={0.5} data-id="element-141" />
            <BenefitCard icon={Star} title="Herramienta de Vida" description="Una práctica sagrada que podrás usar siempre." delay={0.6} data-id="element-142" />
          </div>
        </div>
      </section>

      {/* 5. OFFER / PRICE SECTION */}
      <section id="offer" className="py-24 px-4 bg-gradient-to-b from-rose-50 via-violet-50/50 to-white" data-id="element-143">
        <div className="max-w-lg mx-auto" data-id="element-144">
          <FadeIn data-id="element-145">
            <div className="relative bg-white rounded-3xl shadow-2xl border border-violet-100/50 overflow-hidden" data-id="element-146">
              {/* Top gradient bar */}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-violet-500 via-purple-500 to-rose-500" data-id="element-147" />

              <div className="p-8 md:p-10 text-center" data-id="element-148">
                {/* Offer Badge */}
                <span className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-bold px-5 py-2 rounded-full text-sm mb-8 shadow-lg shadow-violet-200" data-id="element-149">
                  <Flame className="w-4 h-4" data-id="element-150" />
                  OFERTA ESPECIAL — 75% OFF
                </span>

                {/* Main Title */}
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-8 font-[Montserrat,_sans-serif]" style={{
                lineHeight: '1.3'
              }} data-id="element-151">
                  Accede hoy a un precio único
                </h2>

                {/* Strikethrough Price */}
                <p className="text-lg text-gray-400 mb-2" data-id="element-152">
                  <span className="line-through" data-id="element-153">Precio regular: 68 €</span>
                </p>

                {/* Main Price */}
                <div className="mb-4" data-id="element-154">
                  <span className="text-6xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600" data-id="element-155">
                    17 €
                  </span>
                </div>

                {/* Clarification Text */}
                <p className="text-gray-500 mb-8 text-sm" data-id="element-156">
                  Pago único • Acceso de por vida
                </p>

                {/* Scarcity Message */}
                <div className="inline-flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-600 font-medium px-5 py-3 rounded-xl mb-8" data-id="element-157">
                  Solo quedan 23 copias con descuento
                  <Flame className="w-4 h-4" data-id="element-158" />
                </div>

                {/* Countdown Timer */}
                <div className="mb-8" data-id="element-159">
                  <CountdownTimer data-id="element-160" />
                </div>

                {/* CTA Button */}
                <Button fullWidth onClick={goToCheckout} className="text-base py-5 shadow-xl shadow-violet-300/50 mb-6 max-w-sm mx-auto hover:shadow-violet-400/50" data-id="element-161">
                  <Flame className="w-4 h-4 mr-2" data-id="element-162" />
                  QUIERO ACCEDER AHORA
                </Button>

                {/* Social Proof */}
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-163">
                  <div className="flex text-yellow-400" data-id="element-164">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-165" />)}
                  </div>
                  <span className="text-gray-600 text-sm" data-id="element-166">
                    4.9/5 — opiniones verificadas
                  </span>
                </div>

                {/* Guarantee */}
                <div className="flex items-center justify-center gap-2 text-violet-600" data-id="element-167">
                  <Shield className="w-4 h-4" data-id="element-168" />
                  <span className="text-sm font-medium" data-id="element-169">
                    Garantía de 60 días — Riesgo cero
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 6. COURSE MODULES SECTION */}
      <section className="py-20 px-4 bg-cream" data-id="element-170">
        <div className="max-w-3xl mx-auto" data-id="element-171">
          <FadeIn data-id="element-172">
            <h2 className="text-3xl md:text-4xl font-sans font-medium text-center text-gray-900 mb-8" style={{
            lineHeight: '1.3'
          }} data-id="element-173">
              ¿Qué vas a aprender?
            </h2>
            <div className="flex justify-center mb-12" data-id="element-174">
              <img src="https://cdn.magicpatterns.com/uploads/xbUYtf2LGfMKVcdbxyzaKx/imgi_3_img2.webp" alt="Registros Akáshicos — Biblioteca del alma" className="w-full max-w-[560px] rounded-2xl" style={{
              boxShadow: '0 8px 40px 0 rgba(124, 58, 237, 0.13), 0 2px 12px 0 rgba(0,0,0,0.10)'
            }} data-id="element-175" />
            </div>
          </FadeIn>

          <div className="space-y-4" data-id="element-176">
            {[{
            title: 'Introducción a los Registros Akáshicos',
            desc: 'Comprende qué son, su historia y cómo funcionan.'
          }, {
            title: 'Preparación Energética y Protección',
            desc: 'Aprende a elevar tu vibración y crear un espacio seguro.'
          }, {
            title: 'Oración Sagrada de Apertura',
            desc: 'La llave maestra para acceder a la dimensión akáshica.'
          }, {
            title: 'Lectura e Interpretación',
            desc: 'Técnicas para recibir y decodificar los mensajes de tu alma.'
          }, {
            title: 'Sanación de Patrones Kármicos',
            desc: 'Identifica y libera la raíz de tus bloqueos recurrentes.'
          }, {
            title: 'Conexión con Guías Espirituales',
            desc: 'Conoce a tus maestros y recibe su asistencia amorosa.'
          }, {
            title: 'Aplicación práctica en la vida diaria',
            desc: 'Integra esta sabiduría en tus decisiones cotidianas.'
          }].map((module, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-177">
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-start hover:border-violet-200 transition-colors" data-id="element-178">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-sans font-semibold text-sm mr-4 mt-1" data-id="element-179">
                    {i + 1}
                  </div>
                  <div data-id="element-180">
                    <h3 className="font-sans font-medium text-gray-900 text-lg mb-1" style={{
                  lineHeight: '1.3'
                }} data-id="element-181">
                      {module.title}
                    </h3>
                    <p className="font-sans font-normal text-gray-500 text-sm" style={{
                  lineHeight: '1.6'
                }} data-id="element-182">
                      {module.desc}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 7. GUARANTEE SECTION */}
      <section className="py-20 px-4 bg-rose-50/50" data-id="element-183">
        <div className="max-w-3xl mx-auto" data-id="element-184">
          <FadeIn data-id="element-185">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-lg text-center border border-rose-100 relative overflow-hidden" data-id="element-186">
              <div className="absolute top-0 right-0 p-8 opacity-5" data-id="element-187">
                <Shield className="w-40 h-40 text-rose-500" data-id="element-188" />
              </div>
              <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6 text-rose-500" data-id="element-189">
                <Shield className="w-10 h-10" data-id="element-190" />
              </div>
              <h2 className="text-2xl md:text-3xl font-sans font-medium text-gray-900 mb-4" style={{
              lineHeight: '1.3'
            }} data-id="element-191">
                Garantía Total de 60 Días
              </h2>
              <p className="font-sans font-normal text-gray-600 mb-8 max-w-[680px] mx-auto" style={{
              lineHeight: '1.6'
            }} data-id="element-192">
                Explora el contenido, aplica lo aprendido y evalúa tu
                experiencia. Si no quedas satisfecho por cualquier razón, puedes
                solicitar el reembolso completo. <br data-id="element-193" />
                <span className="font-semibold text-rose-500" data-id="element-194">
                  Riesgo cero para el alumno.
                </span>
              </p>
              <div className="inline-flex items-center text-sm font-bold text-gray-400 uppercase tracking-widest" data-id="element-195">
                <Check className="w-4 h-4 mr-2 text-green-500" data-id="element-196" />
                Compra Protegida
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 8. TESTIMONIALS SECTION */}
      <section className="py-24 px-4 bg-violet-50/30" data-id="element-197">
        <div className="max-w-6xl mx-auto" data-id="element-198">
          <FadeIn data-id="element-199">
            <h2 className="text-3xl md:text-4xl font-sans font-medium text-center text-gray-900 mb-16" style={{
            lineHeight: '1.3'
          }} data-id="element-200">
              Lo que dicen quienes ya accedieron
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-201">
            <TestimonialCard name="Luciana Martínez" location="Buenos Aires, 34 años" quote="Siempre sentí que cargaba emociones que no eran mías. Después de aprender a abrir mis registros, entendí que arrastraba un patrón de abandono de tres generaciones atrás. Lo trabajé con las técnicas del curso y por primera vez en años dejé de sabotear mis relaciones de pareja. Mi vida cambió completamente." delay={0.1} data-id="element-202" />
            <TestimonialCard name="Roberto Figueroa" location="Madrid, 41 años" quote="No tenía ninguna experiencia en temas espirituales y tenía muchas dudas. Pero el método es tan claro y paso a paso que en la segunda semana ya estaba recibiendo mensajes muy concretos en mis meditaciones. Hoy uso los registros antes de cada decisión importante y siento una paz que nunca había experimentado." delay={0.2} data-id="element-203" />
            <TestimonialCard name="Valentina Sosa" location="México DF, 28 años" quote="Llevaba meses con una ansiedad terrible y ningún terapeuta podía ayudarme del todo. A través de los registros descubrí un voto de sufrimiento de una vida pasada que estaba bloqueando mi bienestar. Lo liberé con el ejercicio del módulo 5 y la diferencia fue inmediata. Lo recomiendo de corazón." delay={0.3} data-id="element-204" />
          </div>
        </div>
      </section>

      {/* 9. WHAT'S INCLUDED SECTION */}
      <section className="py-20 px-4 bg-white" data-id="element-205">
        <div className="max-w-4xl mx-auto text-center" data-id="element-206">
          <FadeIn data-id="element-207">
            <h2 className="text-3xl md:text-4xl font-sans font-medium text-center text-gray-900 mb-12" style={{
            lineHeight: '1.3'
          }} data-id="element-208">
              ¿Qué incluye tu acceso?
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12" data-id="element-209">
            {[{
            icon: Smartphone,
            text: 'Acceso desde celular y computadora'
          }, {
            icon: Mail,
            text: 'Entrega inmediata por email'
          }, {
            icon: InfinityIcon,
            text: 'Acceso de por vida + actualizaciones'
          }, {
            icon: RefreshCw,
            text: 'Metodología probada y transformadora'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-210">
                <div className="flex items-center p-6 bg-gray-50 rounded-xl border border-gray-100" data-id="element-211">
                  <item.icon className="w-8 h-8 text-violet-500 mr-4 flex-shrink-0" data-id="element-212" />
                  <span className="font-sans font-normal text-gray-800 text-left" style={{
                lineHeight: '1.6'
              }} data-id="element-213">
                    {item.text}
                  </span>
                </div>
              </FadeIn>)}
          </div>
          <FadeIn delay={0.4} data-id="element-214">
            <Button onClick={scrollToOffer} className="px-12" data-id="element-215">
              👉 QUIERO ACCEDER AHORA
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* 10. FAQ SECTION */}
      <section className="py-20 px-4 bg-cream" data-id="element-216">
        <div className="max-w-3xl mx-auto" data-id="element-217">
          <FadeIn data-id="element-218">
            <h2 className="text-3xl md:text-4xl font-sans font-medium text-center text-gray-900 mb-12" style={{
            lineHeight: '1.3'
          }} data-id="element-219">
              Preguntas frecuentes
            </h2>
          </FadeIn>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8" data-id="element-220">
            <AccordionItem question="¿Por cuánto tiempo tengo acceso?" answer="Tienes acceso de por vida al curso. Una vez que realizas tu pago, el material es tuyo para siempre, incluyendo todas las actualizaciones futuras que realicemos en la plataforma." isOpen={openFaqIndex === 0} onClick={() => setOpenFaqIndex(openFaqIndex === 0 ? null : 0)} data-id="element-221" />
            <AccordionItem question="¿Cómo accedo al material?" answer="Recibirás un email con tus datos de acceso inmediatamente después de realizar el pago. Podrás acceder a nuestra plataforma privada desde cualquier dispositivo con conexión a internet (móvil, tablet o computadora)." isOpen={openFaqIndex === 1} onClick={() => setOpenFaqIndex(openFaqIndex === 1 ? null : 1)} data-id="element-222" />
            <AccordionItem question="¿Qué pasa si necesito ayuda?" answer="No estarás solo/a. Contamos con un equipo de soporte dedicado por email para resolver cualquier duda técnica o de contenido que puedas tener durante tu proceso de aprendizaje." isOpen={openFaqIndex === 2} onClick={() => setOpenFaqIndex(openFaqIndex === 2 ? null : 2)} data-id="element-223" />
            <AccordionItem question="¿Cuándo recibo el acceso?" answer="El acceso es inmediato y automático. En menos de 5 minutos después de confirmar tu pago, el sistema te enviará el email de bienvenida con tus credenciales de acceso." isOpen={openFaqIndex === 3} onClick={() => setOpenFaqIndex(openFaqIndex === 3 ? null : 3)} data-id="element-224" />
          </div>
        </div>
      </section>

      {/* 11. FINAL CLOSE SECTION */}
      <section className="py-24 px-4 bg-gradient-to-br from-violet-900 via-violet-800 to-purple-900 text-white text-center relative overflow-hidden" data-id="element-225">
        <div className="absolute inset-0 opacity-20" data-id="element-226">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-227"></div>
        </div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-228">
          <FadeIn data-id="element-229">
            <h2 className="md:text-5xl font-semibold mb-6 text-[20px] font-[Montserrat,_sans-serif]" style={{
            letterSpacing: '-0.5px',
            lineHeight: '1.1'
          }} data-id="element-230">
              Tu transformación comienza hoy
            </h2>
            <p className="font-sans font-normal text-violet-100 mb-10 max-w-[680px] mx-auto text-[20px]" style={{
            lineHeight: '1.6'
          }} data-id="element-231">
              Accede a la sabiduría infinita que ya existe dentro de ti.
            </p>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-232">
            <div className="font-bold text-white mb-10 text-[48px]" data-id="element-233">17 €</div>
            <Button variant="white" onClick={goToCheckout} className="text-base px-12 py-5 mb-8 text-violet-900 hover:text-violet-800" data-id="element-234">
              👉 QUIERO ACCEDER AHORA
            </Button>
            <div className="flex justify-center items-center gap-4 text-sm text-violet-200" data-id="element-235">
              <div className="flex text-yellow-400" data-id="element-236">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-237" />)}
              </div>
              <span className="text-[12px]" data-id="element-238">4.9/5 opiniones verificadas</span>
              <span className="mx-2 opacity-50" data-id="element-239">|</span>
              <Shield className="w-4 h-4" data-id="element-240" />
              <span className="text-[14px]" data-id="element-241">Garantía de 60 días</span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* STICKY MOBILE CTA */}
      <AnimatePresence data-id="element-242">
        {showStickyBar && <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="fixed bottom-0 left-0 w-full md:hidden z-50" style={{
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)'
      }} data-id="element-243">
            {/* Green urgency strip */}
            <div className="w-full bg-emerald-600 py-2 px-4 flex items-center justify-center gap-2" data-id="element-244">
              <span className="text-white font-bold text-sm tracking-wide flex items-center gap-1.5" data-id="element-245">
                ⏰ La oferta termina HOY
              </span>
            </div>
            {/* CTA button area */}
            <div className="w-full bg-white px-4 py-3" data-id="element-246">
              <a href={CHECKOUT_URL} onClick={e => {
            e.preventDefault();
            const eventId = generateEventId();
            if (window.fbq) {
              // Standard Meta event for ad optimization
              window.fbq('track', 'InitiateCheckout', {
                value: 6.36,
                currency: 'USD',
                content_ids: ['57859915579773'],
                content_type: 'product',
                num_items: 1
              }, {
                eventID: eventId
              });
              // Custom event for internal tracking
              window.fbq('trackCustom', 'Click_Ir_Shopify', {
                value: 6.36,
                currency: 'USD',
                content_ids: ['57859915579773'],
                destination_url: CHECKOUT_URL
              }, {
                eventID: eventId
              });
            }
            // Small delay to ensure pixel fires before navigation
            setTimeout(() => {
              window.location.href = CHECKOUT_URL;
            }, 300);
          }} className="w-full py-4 rounded-lg bg-orange-500 hover:bg-orange-600 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-orange-300/40 flex items-center justify-center gap-2 no-underline" data-id="element-247">
                <span className="text-white font-bold text-lg tracking-wide flex items-center justify-center gap-2" data-id="element-248">
                  👉 COMPRAR AHORA
                </span>
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}